import type { ExtensionValue, SchemaFieldType } from '@erp/shared-types';

const MAX_TEXT_LENGTH = 1000;
export const EMPTY_EXTENSION_VALUE = '—';
type FormatOptions = { currency?: string; yes?: string; no?: string };

/** Keep locale/options-specific formatters shared across cells and detail renders. */
function cachedFormatter<Options, Formatter>(
  Constructor: new (locale: string, options: Options) => Formatter,
) {
  const formatters = new Map<string, Formatter>();
  return (locale: string, options: Options) => {
    const key = JSON.stringify([locale, options]);
    let formatter = formatters.get(key);
    if (formatter === undefined) {
      formatter = new Constructor(locale, options);
      formatters.set(key, formatter);
    }
    return formatter;
  };
}

const numberFormatter = cachedFormatter<
  Intl.NumberFormatOptions,
  Intl.NumberFormat
>(Intl.NumberFormat);
const dateFormatter = cachedFormatter<
  Intl.DateTimeFormatOptions,
  Intl.DateTimeFormat
>(Intl.DateTimeFormat);

/**
 * Sign and currency symbol around the digits. A currency code Intl rejects
 * (free text such as '$' or 'US Dollars') formats as a plain number instead
 * of throwing during render.
 */
function affixFormatter(locale: string, currency: string | undefined) {
  if (currency) {
    try {
      return numberFormatter(locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      });
    } catch (error) {
      if (!(error instanceof RangeError)) throw error;
    }
  }
  return numberFormatter(locale, { maximumFractionDigits: 0 });
}

/** Group exact decimal strings without ever converting the amount to a JS number. */
function decimalText(
  value: string | number,
  locale: string,
  currency?: string,
) {
  const text = String(value);
  const negative = text.startsWith('-');
  const [whole, fraction] = text.replace(/^-/, '').split('.');
  const integer = numberFormatter(locale, {
    maximumFractionDigits: 0,
  }).format(BigInt(whole));
  const digits = numberFormatter(locale, { useGrouping: false });
  const decimal =
    digits.formatToParts(1.1).find((part) => part.type === 'decimal')?.value ??
    '.';
  const exact =
    integer +
    (fraction === undefined
      ? ''
      : decimal +
        fraction
          .split('')
          .map((digit) => digits.format(Number(digit)))
          .join(''));
  const affixes = affixFormatter(locale, currency).formatToParts(
    negative ? -1 : 1,
  );
  return affixes
    .map((part) => (part.type === 'integer' ? exact : part.value))
    .join('');
}

function scalarText(
  value: string | number,
  fieldType: SchemaFieldType,
  locale: string,
  options: FormatOptions,
) {
  switch (fieldType) {
    case 'currency':
      return decimalText(value, locale, options.currency);
    case 'numeric':
    case 'integer':
      return decimalText(value, locale);
    case 'float':
      return typeof value === 'number'
        ? numberFormatter(locale, {
            maximumSignificantDigits: 17,
          }).format(value)
        : value;
    case 'date':
    case 'datetime':
      return dateText(value, fieldType, locale);
    default:
      return String(value);
  }
}

function dateText(
  value: string | number,
  type: 'date' | 'datetime',
  locale: string,
) {
  return dateFormatter(
    locale,
    type === 'date'
      ? { dateStyle: 'medium', timeZone: 'UTC' }
      : { dateStyle: 'medium', timeStyle: 'short' },
  ).format(new Date(value));
}

function valueText(
  value: ExtensionValue,
  fieldType: SchemaFieldType,
  locale: string,
  options: FormatOptions,
) {
  if (typeof value === 'boolean')
    return value ? (options.yes ?? 'true') : (options.no ?? 'false');
  if (typeof value === 'string' || typeof value === 'number')
    return scalarText(value, fieldType, locale, options);
  return JSON.stringify(value);
}

export function formatExtensionValue(
  value: ExtensionValue | undefined,
  fieldType: SchemaFieldType,
  locale: string,
  options: FormatOptions = {},
): string {
  if (value === null || value === undefined || value === '')
    return EMPTY_EXTENSION_VALUE;
  const text = valueText(value, fieldType, locale, options);
  return text.length > MAX_TEXT_LENGTH
    ? text.slice(0, MAX_TEXT_LENGTH) + '…'
    : text;
}
